/**
 * AddBulk Validation Schema
 *
 * Yup validation schemas for bulk upload forms.
 * Separated from AddBulk.tsx for better testability.
 */
import * as Yup from "yup";
import { requiredString, requiredId } from "helpers/validation/yupSchemaFactory";
import { PAGES } from "./AddBulk.types";
import { SelectOption } from "components/dropdown/Dropdown";

/**
 * Helper function to get contact type label by ID
 * @param contactTypesId - The contact type ID to lookup
 * @param contactAccessTypes - The list of contact access types
 * @returns The lowercased trimmed label or undefined
 */
export const getContactTypeLabelById = (
  contactTypesId: number | undefined,
  contactAccessTypes: SelectOption[]
): string | undefined => {
  if (!contactTypesId) return undefined;
  return contactAccessTypes
    .find((type: SelectOption) => type.value === contactTypesId)
    ?.label.trim()
    .toLowerCase();
};

/**
 * Factory function to create validation schema based on upload type
 * @param noun - The upload type (SITE, CONTACT, SURVEY, etc.)
 * @param contactAccessTypes - The list of contact access types (for CONTACT validation)
 * @returns Yup validation schema
 */
export const getValidationSchema = (noun: PAGES, contactAccessTypes: SelectOption[] = []) => {
  // Base schema - all upload types require a CSV file
  const baseSchema = {
    csvFile: Yup.mixed().required("Please provide a CSV File"),
  };

  switch (noun) {
    case PAGES.CONTACT:
      return Yup.object({
        ...baseSchema,
        contactTypesId: requiredId("Please select a contact type"),
        sitesID: Yup.number()
          .nullable()
          .when("contactTypesId", {
            is: (val: number) => getContactTypeLabelById(val, contactAccessTypes) === "site",
            then: (schema) => schema.required("Please provide a site"),
            otherwise: (schema) => schema,
          }),
        projectsID: Yup.number()
          .nullable()
          .when("contactTypesId", {
            is: (val: number) => getContactTypeLabelById(val, contactAccessTypes) === "project",
            then: (schema) => schema.required("Please provide a project"),
            otherwise: (schema) => schema,
          }),
      });

    case PAGES.SURVEY:
      return Yup.object({
        ...baseSchema,
        action: requiredString("Please provide an action"),
        programmesId: requiredId("Please provide a programme"),
        surveyTemplatesId: requiredId("Please provide a survey template"),
      });

    case PAGES.SITE:
    case PAGES.ITEM:
    case PAGES.ASSET:
    case PAGES.CATALOGUE_ITEMS:
    case PAGES.USER:
    default:
      // For all other types, only CSV file validation is required
      return Yup.object(baseSchema);
  }
};
